import { ref, computed } from "vue";
import Decimal from "decimal.js";

/**
 * 最大输入长度（数字位数）
 */
const MAX_INPUT_LENGTH = 16;

/**
 * 操作符类型
 */
export type OperatorType = "+" | "-" | "*" | "/" | "=" | null;

/**
 * 计算器按键类型
 */
export type ButtonType = "number" | "operator" | "function" | "clear";

/**
 * 计算器状态接口
 */
export interface CalculatorState {
  display: string;
  previousValue: string;
  currentOperator: OperatorType;
  waitingForNewValue: boolean;
  history: string[];
  memory: string;
  isError: boolean;
  errorMessage: string;
}

/**
 * 计算器 Hook
 * 使用 Vue3 Composition API 实现状态管理，替代 Pinia
 */
export function useCalculator() {
  // 状态定义
  const display = ref("0");
  const previousValue = ref("");
  const currentOperator = ref<OperatorType>(null);
  const waitingForNewValue = ref(false);
  const history = ref<string[]>([]);
  const memory = ref("0");
  const isError = ref(false);
  const errorMessage = ref("");
  const expression = ref("");

  // 配置Decimal.js精度
  Decimal.set({ precision: 20 });

  /**
   * 计算结果显示文本
   */
  const displayText = computed(() => {
    if (isError.value) {
      return errorMessage.value || "错误";
    }
    return formatNumber(display.value);
  });

  /**
   * 表达式显示文本
   */
  const expressionText = computed(() => {
    return expression.value || "";
  });

  /**
   * 格式化数字显示
   */
  function formatNumber(value: string): string {
    if (!value || value === "0") return "0";

    try {
      const decimal = new Decimal(value);
      
      // 强制使用 toFixed() 避免科学计数法，对于大数使用足够的精度
      let formatted: string;
      if (decimal.isInteger()) {
        // 对于整数，直接使用 toFixed(0) 确保不会有科学计数法
        formatted = decimal.toFixed(0);
      } else {
        // 对于小数，保留必要的小数位数
        formatted = decimal.toFixed();
      }

      // 只移除小数部分末尾的零，不影响整数部分
      let trimmed = formatted;
      if (formatted.includes(".")) {
        trimmed = formatted.replace(/\.?0+$/, "");
      }

      // 添加千位分隔符
      const parts = trimmed.split(".");
      if (parts[0]) {
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }

      return parts.join(".");
    } catch {
      return value;
    }
  }

  /**
   * 输入数字
   */
  function inputNumber(num: string): void {
    if (isError.value) {
      clearAll();
    }

    // 如果表达式以=结尾，说明上一次计算已完成，开始新计算
    if (expression.value.endsWith("=")) {
      expression.value = "";
    }

    if (waitingForNewValue.value) {
      display.value = num;
      waitingForNewValue.value = false;
    } else {
      const newValue = display.value === "0" ? num : display.value + num;
      // 限制最大输入长度（不包括小数点和负号）
      const digitCount = newValue.replace(/[.-]/g, "").length;
      if (digitCount <= MAX_INPUT_LENGTH) {
        display.value = newValue;
      }
    }
  }

  /**
   * 输入小数点
   */
  function inputDecimal(): void {
    if (isError.value) {
      clearAll();
    }

    // 如果表达式以=结尾，说明上一次计算已完成，开始新计算
    if (expression.value.endsWith("=")) {
      expression.value = "";
    }

    if (waitingForNewValue.value) {
      display.value = "0.";
      waitingForNewValue.value = false;
    } else if (!display.value.includes(".")) {
      const newValue = display.value + ".";
      // 限制最大输入长度（不包括小数点和负号）
      const digitCount = newValue.replace(/[.-]/g, "").length;
      if (digitCount <= MAX_INPUT_LENGTH) {
        display.value = newValue;
      }
    }
  }

  /**
   * 输入操作符
   */
  function inputOperator(operator: OperatorType): void {
    if (isError.value) {
      clearAll();
      return;
    }

    const current = display.value;
    let originalPreviousValue = previousValue.value;
    let originalOperator = currentOperator.value;

    if (
      previousValue.value &&
      currentOperator.value &&
      !waitingForNewValue.value
    ) {
      calculate();
      // 计算后，将结果作为下一次计算的前值
      previousValue.value = display.value;
    } else {
      previousValue.value = current;
    }

    if (operator === "=") {
      // 只有在有有效的计算操作时才执行等号逻辑
      if (originalPreviousValue && originalOperator) {
        // 记录到历史
        const expr = `${originalPreviousValue} ${originalOperator} ${current}`;
        history.value.unshift(`${expr} = ${display.value}`);
        if (history.value.length > 50) {
          history.value = history.value.slice(0, 50);
        }

        // 显示完整的计算表达式
        expression.value = `${originalPreviousValue} ${originalOperator} ${current} =`;
        currentOperator.value = null;
        previousValue.value = "";
      }
      // 如果没有有效的计算操作，不做任何处理
    } else {
      expression.value = `${previousValue.value} ${operator}`;
      currentOperator.value = operator;
    }

    waitingForNewValue.value = true;
  }

  /**
   * 执行计算
   */
  function calculate(): void {
    if (!previousValue.value || !currentOperator.value) return;

    try {
      // 检查输入值是否为特殊值
      if (
        previousValue.value === "NaN" ||
        previousValue.value === "Infinity" ||
        display.value === "NaN" ||
        display.value === "Infinity"
      ) {
        throw new Error("无效的数值");
      }

      const prev = new Decimal(previousValue.value);
      const current = new Decimal(display.value);
      let result: Decimal;

      switch (currentOperator.value) {
        case "+":
          result = prev.plus(current);
          break;
        case "-":
          result = prev.minus(current);
          break;
        case "*":
          result = prev.times(current);
          break;
        case "/":
          if (current.isZero()) {
            errorMessage.value = "除数不能为零";
            throw new Error("除数不能为零");
          }
          result = prev.dividedBy(current);
          break;
        case "^" as OperatorType:
          result = new Decimal(Math.pow(prev.toNumber(), current.toNumber()));
          break;
        default:
          return;
      }

      display.value = result.toString();
      isError.value = false;
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      if (!errorMessage.value) {
        errorMessage.value = error instanceof Error ? error.message : "计算错误";
      }
      console.error("计算错误:", error);
    }
  }

  /**
   * 删除单条历史记录
   */
  function removeHistoryItem(index: number): void {
    if (index >= 0 && index < history.value.length) {
      history.value.splice(index, 1);
    }
  }

  /**
   * 清除当前输入
   */
  function clearEntry(): void {
    display.value = "0";
    isError.value = false;
  }

  /**
   * 清除所有
   */
  function clearAll(): void {
    display.value = "0";
    previousValue.value = "";
    currentOperator.value = null;
    waitingForNewValue.value = false;
    expression.value = "";
    isError.value = false;
    errorMessage.value = "";
  }

  /**
   * 退格
   */
  function backspace(): void {
    if (isError.value || waitingForNewValue.value) {
      clearEntry();
      return;
    }

    if (display.value.length > 1) {
      display.value = display.value.slice(0, -1);
    } else {
      display.value = "0";
    }
  }

  /**
   * 正负号切换
   */
  function toggleSign(): void {
    if (isError.value) return;

    if (display.value !== "0") {
      display.value = display.value.startsWith("-")
        ? display.value.slice(1)
        : "-" + display.value;
    }
  }

  /**
   * 百分号
   */
  function percentage(): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);
      const result = current.dividedBy(100);
      display.value = result.toString();
    } catch (error) {
      display.value = "错误";
      isError.value = true;
    }
  }

  /**
   * 平方根
   */
  function squareRoot(): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);
      if (current.isNegative()) {
        errorMessage.value = "负数不能开平方根";
        throw new Error("负数不能开平方根");
      }
      const result = current.sqrt();
      
      // 显示计算公式
      expression.value = `√(${current}) =`;
      
      display.value = result.toString();
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      if (!errorMessage.value) {
        errorMessage.value = error instanceof Error ? error.message : "计算错误";
      }
    }
  }

  /**
   * 平方
   */
  function square(): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);
      const result = current.times(current);
      
      // 显示计算公式
      expression.value = `sqr(${current}) =`;
      
      display.value = result.toString();
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      errorMessage.value = error instanceof Error ? error.message : "计算错误";
    }
  }

  /**
   * 倒数
   */
  function reciprocal(): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);
      if (current.isZero()) {
        errorMessage.value = "零不能求倒数";
        throw new Error("零不能求倒数");
      }
      const result = new Decimal(1).dividedBy(current);
      
      // 显示计算公式
      expression.value = `1/(${current}) =`;
      
      display.value = result.toString();
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      if (!errorMessage.value) {
        errorMessage.value = error instanceof Error ? error.message : "计算错误";
      }
    }
  }

  /**
   * 内存存储
   */
  function memoryStore(): void {
    if (!isError.value) {
      memory.value = display.value;
      waitingForNewValue.value = true;
    }
  }

  /**
   * 内存读取
   */
  function memoryRecall(): void {
    display.value = memory.value;
    waitingForNewValue.value = true;
  }

  /**
   * 内存清除
   */
  function memoryClear(): void {
    memory.value = "0";
  }

  /**
   * 内存加法
   */
  function memoryAdd(): void {
    if (!isError.value) {
      try {
        const memValue = new Decimal(memory.value);
        const currentValue = new Decimal(display.value);
        memory.value = memValue.plus(currentValue).toString();
        waitingForNewValue.value = true;
      } catch (error) {
        console.error("内存加法错误:", error);
      }
    }
  }

  /**
   * 内存减法
   */
  function memorySubtract(): void {
    if (!isError.value) {
      try {
        const memValue = new Decimal(memory.value);
        const currentValue = new Decimal(display.value);
        memory.value = memValue.minus(currentValue).toString();
        waitingForNewValue.value = true;
      } catch (error) {
        console.error("内存减法错误:", error);
      }
    }
  }

  /**
   * 科学计算器函数
   */
  function scientificFunction(func: string): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);
      let result: Decimal;

      switch (func) {
        case "sin":
          // 将角度转换为弧度
          result = new Decimal(Math.sin((current.toNumber() * Math.PI) / 180));
          break;
        case "cos":
          result = new Decimal(Math.cos((current.toNumber() * Math.PI) / 180));
          break;
        case "tan":
          result = new Decimal(Math.tan((current.toNumber() * Math.PI) / 180));
          break;
        case "log":
          if (current.lte(0)) {
            errorMessage.value = "对数的真数必须大于0";
            throw new Error("对数的真数必须大于0");
          }
          result = new Decimal(Math.log10(current.toNumber()));
          break;
        case "ln":
          if (current.lte(0)) {
            errorMessage.value = "自然对数的真数必须大于0";
            throw new Error("自然对数的真数必须大于0");
          }
          result = new Decimal(Math.log(current.toNumber()));
          break;
        case "exp":
          result = new Decimal(Math.exp(current.toNumber()));
          break;
        case "pow":
          // x^y 需要两个操作数，这里先存储底数，等待指数输入
          previousValue.value = current.toString();
          currentOperator.value = "^" as any;
          waitingForNewValue.value = true;
          expression.value = `${current} ^`;
          return;
        default:
          return;
      }

      display.value = result.toString();
      waitingForNewValue.value = true;
      isError.value = false;
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      if (!errorMessage.value) {
        errorMessage.value = error instanceof Error ? error.message : "科学计算错误";
      }
      console.error("科学计算错误:", error);
    }
  }

  /**
   * 阶乘函数
   */
  function factorial(): void {
    if (isError.value) return;

    try {
      const current = new Decimal(display.value);

      // 检查是否为非负整数
      if (current.isNegative() || !current.isInteger()) {
        errorMessage.value = "阶乘只能计算非负整数";
        throw new Error("阶乘只能计算非负整数");
      }

      // 检查数值范围（避免计算过大的阶乘）
      if (current.gt(170)) {
        errorMessage.value = "数值过大，无法计算阶乘";
        throw new Error("数值过大，无法计算阶乘");
      }

      let result = new Decimal(1);
      let n = current.toNumber();

      for (let i = 2; i <= n; i++) {
        result = result.times(i);
      }

      display.value = result.toString();
      waitingForNewValue.value = true;
      isError.value = false;
      errorMessage.value = "";
    } catch (error) {
      display.value = "错误";
      isError.value = true;
      if (!errorMessage.value) {
        errorMessage.value = error instanceof Error ? error.message : "阶乘计算错误";
      }
      console.error("阶乘计算错误:", error);
    }
  }

  return {
    // 状态
    display,
    previousValue,
    currentOperator,
    waitingForNewValue,
    history,
    memory,
    isError,
    errorMessage,
    expression,

    // 计算属性
    displayText,
    expressionText,

    // 方法
    inputNumber,
    inputDecimal,
    inputOperator,
    calculate,
    removeHistoryItem,
    clearEntry,
    clearAll,
    backspace,
    toggleSign,
    percentage,
    squareRoot,
    square,
    reciprocal,
    memoryStore,
    memoryRecall,
    memoryClear,
    memoryAdd,
    memorySubtract,
    formatNumber,
    scientificFunction,
    factorial,
  };
}

/**
 * 导出类型，保持向后兼容
 */
export { useCalculator as useCalculatorStore };